import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Eye, FileText, Loader2, X } from "lucide-react";
import { api } from "@/lib/api";
import { DocumentoPreviewModal } from "@/components/DocumentoPreviewModal";
import { formatDate } from "@/components/DetailLayout";

// ============================================================================
// Documentos finales de una solicitud (mejora general 2026-07-13).
// Componente ÚNICO y reutilizable, parametrizado por el tipo de servicio:
// el backend resuelve la etiqueta de la acción y los tipos permitidos desde
// kernel.servicio (documento_resultado + tipos_documento_final), de modo que
// un servicio nuevo solo requiere configuración de catálogo.
// ============================================================================

interface DocFinal {
  documentoId: string;
  nombreArchivo: string;
  tipoCodigo: string;
  tipoNombre: string;
  fechaCarga: string;
  cargadoPor: string | null;
}

export function DocumentosFinalesModal({
  solicitudId,
  folio,
  onClose,
}: {
  solicitudId: string;
  folio?: string | null;
  onClose: () => void;
}) {
  const [previewDocId, setPreviewDocId] = useState<string | null>(null);

  const q = useQuery<{ accion: string; documentos: DocFinal[] }>({
    queryKey: ["documentos-finales", solicitudId],
    queryFn: async () =>
      (await api.get<{ accion: string; documentos: DocFinal[] }>(
        `/api/sector/telecom/solicitudes/${solicitudId}/documentos-finales`,
      )).data,
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-full w-full max-w-2xl flex-col rounded-lg bg-surface shadow-overlay">
        <div className="flex shrink-0 items-start justify-between border-b px-5 py-3">
          <div>
            <h2 className="text-base font-semibold text-fg">
              {q.data?.accion ?? "Documentos finales"}
            </h2>
            {folio && <p className="mt-0.5 text-xs text-fg-subtle">Solicitud {folio}</p>}
          </div>
          <button
            onClick={onClose}
            className="rounded p-1 text-fg-faint hover:bg-surface-2 hover:text-fg-muted"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {q.isLoading ? (
            <div className="flex items-center gap-2 p-2 text-sm text-fg-subtle">
              <Loader2 className="h-4 w-4 animate-spin" /> Cargando documentos…
            </div>
          ) : (q.data?.documentos ?? []).length === 0 ? (
            <p className="p-2 text-sm text-fg-subtle">
              No hay documentos finales cargados para esta solicitud.
            </p>
          ) : (
            <ul className="divide-y rounded border">
              {q.data!.documentos.map((d) => (
                <li key={d.documentoId} className="flex items-center justify-between gap-3 px-3 py-2.5">
                  <div className="flex min-w-0 items-center gap-2">
                    <FileText className="h-4 w-4 shrink-0 text-fg-faint" />
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-fg" title={d.nombreArchivo}>
                        {d.nombreArchivo}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-2 text-[11px] text-fg-subtle">
                        <span>{d.tipoNombre}</span>
                        <span>· Cargado {formatDate(d.fechaCarga, true)}</span>
                        {d.cargadoPor && <span>· por {d.cargadoPor}</span>}
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setPreviewDocId(d.documentoId)}
                    className="inline-flex shrink-0 cursor-pointer items-center gap-1 rounded border border-edge px-2 py-1 text-xs text-fg-muted transition hover:bg-surface-2"
                  >
                    <Eye className="h-3 w-3" /> Ver / descargar
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex shrink-0 justify-end border-t px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-edge px-3 py-1.5 text-sm text-fg-muted transition hover:bg-surface-2"
          >
            Cerrar
          </button>
        </div>
      </div>

      {previewDocId && (
        <DocumentoPreviewModal documentoId={previewDocId} onClose={() => setPreviewDocId(null)} />
      )}
    </div>
  );
}
